'use client';
import Dropzone, { Accept, FileRejection } from 'react-dropzone';

import { getCookie } from '@/app/actions/cookies';
import { User } from '@/app/actions/user';
import {
  getLanguageList,
  LanguageItem,
  PreTranslateInfo,
} from '@/app/services/translation';
import TranslationFileOptionsModal from '@/components/translate/translation-file-options-modal';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { siteConfig } from '@/config/site';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useMaintenance } from '@/hooks/use-maintenance';
import { useUploadFile } from '@/lib/upload';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
import { sendGTMEvent } from '@next/third-parties/google';
import {
  HardDriveUploadIcon,
  Loader2,
  MousePointerSquareDashed,
} from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';
import { toast } from 'sonner';
// 文件上传最大限制（100MB）
const MAX_FILE_SIZE = 100 * 1024 * 1024;
const UploadFile = ({
  children,
  showUploadIcon,
  className,
  accept,
  noClick = false,
}: {
  children: ReactNode;
  showUploadIcon?: boolean;
  className?: string;
  accept?: Accept;
  noClick?: boolean;
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileInfo, setFileInfo] = useState<PreTranslateInfo | null>(null);
  const [languageOptions, setLanguageOptions] = useState<LanguageItem[]>([]);

  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { user, triggerLogin } = useAuth();
  const searchParams = useSearchParams();
  const fileUrl = searchParams.get('fileUrl');
  const { uploadFile, isUploading } = useUploadFile();
  const { fetchMaintenanceStatus, showMaintenance } = useMaintenance();
  const getLanguageOptions = async () => {
    try {
      const { data } = await getLanguageList();
      setLanguageOptions(data.file_translation_language);
    } catch {}
  };

  const uploadByFileUrl = async () => {
    const startUpload = (fileUrl: string) => {
      uploadFile({
        fileUrl: fileUrl,
        onUploadProgress: setUploadProgress,
        onClientUploadComplete: async (data) => {
          setFileInfo(data);
          onOpen();
        },
      });

      setIsDragOver(false);
    };
    if (fileUrl) {
      const token = await getCookie(siteConfig.tokenName);
      if (!user && !token && triggerLogin) {
        triggerLogin(() => startUpload(fileUrl));
      } else {
        if (token && !user) {
          // 等待AuthProvider完成用户信息加载
          // 这里不需要做任何事情，useEffect会在user加载完成后处理
          return;
        }
        startUpload(fileUrl);
      }
    }
  };

  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    const [file] = rejectedFiles;
    if (file.errors.some((error) => error.code === 'file-too-large')) {
      toast.warning('文件大小超过限制', {
        position: 'top-center',
      });
    } else {
      toast.warning('请上传支持的文件类型', {
        position: 'top-center',
      });
    }
    setIsDragOver(false);
  };

  const onDropAccepted = async (acceptedFiles: File[]) => {
    const maintenanceStatus = await fetchMaintenanceStatus('file_translation');

    if (maintenanceStatus && maintenanceStatus.is_maintenance) {
      showMaintenance(maintenanceStatus.title, maintenanceStatus.content);
      setIsDragOver(false);
      return;
    }

    const startUpload = (user?: User) => {
      const [file] = acceptedFiles;
      let maxSize = 5 * 1024 * 1024; // 5MB
      if (user?.membership) maxSize = MAX_FILE_SIZE; // 100MB
      if (file.size > maxSize) {
        toast.warning('文件大小超过限制', {
          position: 'top-center',
        });
        setIsDragOver(false);
        return;
      }
      uploadFile({
        file: file,
        onUploadProgress: setUploadProgress,
        onClientUploadComplete: async (data) => {
          if (data.source_n_tokens === 0) {
            toast.warning('暂不支持此类文件翻译', {
              position: 'top-center',
            });
            return;
          }
          setFileInfo(data);
          onOpen();
          sendGTMEvent({
            event: 'file_upload_success',
            value: data.source_file_name,
          });
        },
      });

      setIsDragOver(false);
    };

    if (!user) {
      triggerLogin && triggerLogin((user) => startUpload(user));
      return;
    } else {
      startUpload(user);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void getLanguageOptions();
      if (fileUrl) {
        void uploadByFileUrl();
      }
    }, 0);

    return () => window.clearTimeout(timer);
  }, [fileUrl, user]);

  return (
    <div
      className={cn(
        'relative h-full flex-1 my-16 w-full rounded-xl border-2 border-dashed flex justify-center flex-col items-center cursor-pointer',
        { 'ring-blue-900/25 bg-blue-900/10': isDragOver },
        className,
      )}
    >
      <div className="relative flex flex-1 flex-col items-center justify-center w-full">
        <Dropzone
          maxSize={MAX_FILE_SIZE}
          onDropRejected={onDropRejected}
          onDropAccepted={onDropAccepted}
          accept={accept}
          onDragEnter={() => setIsDragOver(true)}
          onDragLeave={() => setIsDragOver(false)}
          noClick={noClick}
        >
          {({ getRootProps, getInputProps, open }) => (
            <div
              className="h-full w-full flex-1 flex flex-col items-center justify-center gap-y-1 py-8"
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              {isDragOver ? (
                <div className="pt-16">
                  <MousePointerSquareDashed className="h-12 w-12 text-zinc-500 mb-2" />
                </div>
              ) : isUploading ? (
                <div className="pt-16">
                  <Loader2 className="animate-spin h-12 w-12 text-zinc-500 mb-2" />
                </div>
              ) : (
                children
              )}
              <div className="flex flex-col justify-center mb-2 text-sm text-zinc-700 dark:text-zinc-400">
                {isUploading ? (
                  <div className="flex flex-col items-center pb-16 pt-1.5">
                    <p>正在上传...</p>
                    <Progress
                      aria-label="Upload progress"
                      className="mt-2 w-40 h-2"
                      value={uploadProgress}
                    />
                  </div>
                ) : isDragOver ? (
                  <p className="pb-16 pt-1.5">
                    <span className="font-semibold">松开后开始处理</span>
                  </p>
                ) : (
                  showUploadIcon && (
                    <div className="flex flex-col items-center gap-y-4 pt-8">
                      <Button
                        size="lg"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!user && triggerLogin) {
                            triggerLogin(() => open());
                            return;
                          } else {
                            open();
                          }
                        }}
                        className="w-60"
                      >
                        <HardDriveUploadIcon className="w-6 h-6" />
                        上传文件
                      </Button>
                      <p>点击上传文件或把本地文件拖到这里</p>
                    </div>
                  )
                )}
              </div>

              {/*<div className="-mt-4">*/}
              {/*  <AiIdentification text="上传文件将由 AI 翻译处理，建议结合原文审阅。" />*/}
              {/*</div>*/}
            </div>
          )}
        </Dropzone>
      </div>
      <TranslationFileOptionsModal
        languageOptions={languageOptions}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        fileInfo={fileInfo!}
        pageSource="file"
      />
    </div>
  );
};

export default UploadFile;
